import React, { useState, useEffect, createContext } from 'react';
import { Tabs } from 'antd';
import PageHeader from '@/components/PageHeader/index';
import { Box } from '@mui/material';
import client from '@/lib/feathers';
import AccountingSubject from '@/types/database/accountingSubject';
import SetFinanceSetting from './components/SetFinanceSetting/index';
import DownloadSettingFile from './components/DownloadSettingFile/index';
import styles from './index.module.scss';

export const communityContext = createContext<{
  communityData:
    | {
        [key: string]: any;
      }
    | undefined;
  accountingSubjects: AccountingSubject[] | undefined;
}>({
  communityData: undefined,
  accountingSubjects: undefined,
});

const getCommunityData = async () => {
  try {
    const res = await client.service('communityData').find({});
    return res.data[0];
  } catch (err) {
    console.error(err);
  }
};

const getSubjects = async () => {
  try {
    const res = await client.service('accountingSubjects').find({});
    return res.data;
  } catch (err) {
    console.error(err);
  }
};

const getDeadlineContent = (
  generateDate: number,
  deadline: number,
  type: 'current' | 'next'
) => {
  if (generateDate < deadline && type === 'current') {
    return `帳單產生日同月${deadline}日`;
  } else if (generateDate > deadline && type === 'next') {
    return `帳單產生日下下個月${deadline}日`;
  } else {
    return `帳單產生日下個月${deadline}日`;
  }
};

const FinanceSetting: React.FC = () => {
  const [communityData, setCommunityData] = useState<
    | {
        [key: string]: any;
      }
    | undefined
  >();
  const [accountingSubjects, setAccountingSubjects] = useState<
    AccountingSubject[] | undefined
  >();
  const [settingType, setSettingType] = useState<'basic' | 'ecPay'>('basic');
  const [trigger, setTrigger] = useState({});

  useEffect(() => {
    (async () => {
      const data = await getCommunityData();
      setCommunityData(data);
    })();
  }, [trigger]);

  useEffect(() => {
    if (communityData) {
      (async () => {
        const subjects = await getSubjects();
        setAccountingSubjects(subjects);
      })();
    }
  }, [communityData]);

  return (
    <communityContext.Provider value={{ communityData, accountingSubjects }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <PageHeader
          breadcrumbs={[{ name: '財務管理' }, { name: '財務設定' }]}
          title="財務設定"
        />
        {/* 功能按紐區 */}
        <Box>
          <SetFinanceSetting setTrigger={setTrigger} />
        </Box>
        {/* 財務設定顯示區 */}
        {communityData && accountingSubjects && (
          <>
            <Tabs
              onChange={(key: 'basic' | 'ecPay') => setSettingType(key)}
              items={
                communityData.useEcpay
                  ? [
                      {
                        key: 'basic',
                        label: '財務基本設定',
                      },
                      {
                        key: 'ecPay',
                        label: '綠界參數設定',
                      },
                    ]
                  : [
                      {
                        key: 'basic',
                        label: '財務基本設定',
                      },
                    ]
              }
            />
            {settingType === 'basic' ? (
              <Box
                sx={{
                  padding: '10px',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              >
                <h2 className={styles.settingH2}>
                  月繳住戶管理費每坪金額:
                  <span>{communityData.monthPingMoney} 元</span>
                </h2>
                <h2 className={styles.settingH2}>
                  年繳住戶管理費每坪金額:
                  <span>{communityData.yearPingMoney} 元</span>
                </h2>
                <h2 className={styles.settingH2}>
                  月繳店鋪管理費每坪金額:
                  <span>{communityData.shopMonthPingMoney} 元</span>
                </h2>
                <h2 className={styles.settingH2}>
                  月繳汽車清潔費單格金額:
                  <span>{communityData.monthCarMoney} 元</span>
                </h2>
                <h2 className={styles.settingH2}>
                  月繳機車清潔費單格金額:
                  <span>{communityData.monthScooterMoney} 元</span>
                </h2>
                <h2 className={styles.settingH2}>
                  年繳汽車清潔費單格金額:
                  <span>{communityData.yearCarMoney} 元</span>
                </h2>
                <h2 className={styles.settingH2}>
                  年繳機車清潔費單格金額:
                  <span>{communityData.yearScooterMoney} 元</span>
                </h2>
                <h2 className={styles.settingH2}>
                  管理費收入科目:
                  <span>
                    {accountingSubjects.find(
                      (subject) => subject._id === communityData.bankId
                    )?.subjectName || ''}
                  </span>
                </h2>
                <h2 className={styles.settingH2}>
                  匯款戶名:
                  <span>{communityData.payName}</span>
                </h2>
                <h2 className={styles.settingH2}>
                  匯款帳號:
                  <span>{communityData.payId}</span>
                </h2>
                <h2 className={styles.settingH2}>
                  手續費:
                  <span>{communityData.payFee}元</span>
                </h2>
                <h2 className={styles.settingH2}>
                  帳單自動產生日期:
                  <span>每月{communityData.autoGenerateBillDate}日</span>
                </h2>
                <h2 className={styles.settingH2}>
                  帳單截止日期:
                  <span>
                    {getDeadlineContent(
                      communityData.autoGenerateBillDate,
                      communityData.paymentDeadline.date,
                      communityData.paymentDeadline.type
                    )}
                  </span>
                </h2>
                <h2 className={styles.settingH2}>
                  財務設定文件: <DownloadSettingFile data={communityData} />
                </h2>
                <h2 className={styles.settingH2}>
                  開放超商繳費:
                  <span>{communityData.useEcpay ? '開啟' : '關閉'}</span>
                </h2>
              </Box>
            ) : (
              <Box
                sx={{
                  padding: '10px',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                }}
              >
                {communityData.useEcpay && (
                  <>
                    <a
                      href="https://vendor.ecpay.com.tw/User/LogOn_Step1"
                      style={{ fontSize: 20 }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      →前往綠界廠商管理後台
                    </a>
                    <div
                      style={{ fontSize: 18, marginBottom: 20, marginTop: 10 }}
                    >
                      點選系統開發管理/系統介接設定下方有介接資訊，如下圖
                      <img src="/ezcon/img/ecPayExample.png" width="100%" />
                    </div>
                    <h2 className={styles.settingH2}>
                      商店代號:
                      <span>{communityData.MerchantID}</span>
                    </h2>
                    <h2 className={styles.settingH2}>
                      介接HashKey:
                      <span>{communityData.HashKey}</span>
                    </h2>
                    <h2 className={styles.settingH2}>
                      介接HashIV:
                      <span>{communityData.HashIV}</span>
                    </h2>
                  </>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </communityContext.Provider>
  );
};

export default FinanceSetting;
